import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  ToolShell, Field, Select, Result, fmt,
} from "./_shared";
import {
  PV_REGIONS, PUMP_SIZES_KW, PV_PANEL_WATTAGES,
  DAYLIGHT_HOURS, simulateSolarPumpDay, correctFlowForSystemHead,
} from "../../lib/content/solar";
import { hazenWilliamsHeadLoss, pipeVelocity } from "./_shared";

/**
 *  PV ARRAY SIZING — for PV-direct (solar-powered) pump systems
 *  --------------------------------------------------------------
 *  Physics-based simulation, not a rectangle.
 *
 *  Sun follows a half-sine bell across daylight hours, calibrated so its
 *  integral equals the published Peak Sun Hours for the chosen site. The
 *  solar VFD only switches the pump on once DC power crosses the cut-in
 *  threshold (~25 % of pump rated). Between cut-in and rated power the
 *  flow follows the pump curve (Q ∝ P^α, α≈0.5 for a typical centrifugal
 *  under VFD speed control). Above rated, the controller clips power and
 *  flow stays at Q_rated.
 *
 *  Daily volume = trapezoidal integration of Q(t) across the day.
 *  This is the same model the major solar-pump sizing tools use internally —
 *  we just expose every parameter so the visitor can see the staircase of
 *  panels → litres for themselves.
 * --------------------------------------------------------------
 */

const PEAK_FACTOR_DEFAULT = 1.30;   // industry standard for PV-direct pumping
const ETA_DEFAULT_PCT     = 75;     // umbrella system efficiency
const CUT_IN_DEFAULT_PCT  = 25;     // VFD cut-in as % of pump rated kW
const Q30_RATIO_DEFAULT   = 0.55;   // Q at 30 % power / Q_rated for a typical centrifugal

// Hazen-Williams roughness coefficients for the inline single-pipe estimator.
// (Real ZA pump-pipe materials only — sized to cover the 80 % case.)
const PIPE_MATERIALS = [
  { value: "hdpe",        label: "HDPE / Polyethylene · C = 150", C: 150 },
  { value: "pvc",         label: "PVC · C = 150",                 C: 150 },
  { value: "galvanised",  label: "Galvanised steel · C = 120",    C: 120 },
  { value: "steel-old",   label: "Steel · older / mild scale · C = 100", C: 100 },
];

// -----------------------------------------------------------------------------
// Small generic helpers
// -----------------------------------------------------------------------------
function SuffixNum({ value, onChange, suffix, step = "any", min, testid }) {
  // Buffered text-mode input — fixes two UX bugs:
  //   (a) A field with value 2 wouldn't clear (controlled number input
  //       snapped empty → 0 → repaint as "0" before the user finished).
  //       We buffer the raw string locally so the field can sit empty.
  //   (b) "15 becomes 015":  parent state of 0 used to render as "0",
  //       then typing "15" produced "015". We treat 0 as the empty /
  //       placeholder state and never render a leading zero.
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        data-testid={testid}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                 fontSize: "0.95rem", fontWeight: 500 }}
      />
      <span
        className="inline-flex items-center px-3 border border-l-0"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}
      >
        {suffix}
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Searchable location combobox
// -----------------------------------------------------------------------------
function LocationCombobox({ value, onChange, testid }) {
  const selected = PV_REGIONS.find((r) => r.id === value) || null;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PV_REGIONS;
    return PV_REGIONS.filter((r) => `${r.name} ${r.region}`.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);
  useEffect(() => { setActiveIdx(0); }, [query]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  const selectRegion = (r) => { onChange(r.id); setQuery(""); setOpen(false); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (open && filtered[activeIdx]) selectRegion(filtered[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };
  const displayValue = open ? query : (selected ? `${selected.name} · ${selected.region}` : "");

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text" value={displayValue} autoComplete="off"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="Search town or region…"
        data-testid={testid}
        className="w-full px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1] transition-colors"
        style={{ borderColor: open ? "#1FB6C1" : "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.92rem", fontWeight: 500 }}
      />
      {open && (
        <div
          ref={listRef} role="listbox"
          data-testid={testid ? `${testid}-list` : undefined}
          className="absolute left-0 right-0 z-50 mt-1"
          style={{
            background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.14)", borderRadius: 6,
            boxShadow: "0 20px 40px rgba(12,42,64,0.16), 0 1px 2px rgba(12,42,64,0.05)",
            maxHeight: 280, overflowY: "auto",
          }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-3 font-light" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.55)" }}>
              No match for "{query}" — try a nearby town.
            </div>
          ) : (
            filtered.map((r, idx) => {
              const isActive = idx === activeIdx;
              const isSelected = selected && r.id === selected.id;
              return (
                <button
                  key={r.id} type="button" role="option" aria-selected={isSelected}
                  data-idx={idx}
                  data-testid={testid ? `${testid}-opt-${r.id}` : undefined}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectRegion(r)}
                  className="w-full text-left px-3 py-2 flex items-baseline justify-between transition-colors"
                  style={{
                    background: isActive ? "rgba(31,182,193,0.10)" : "transparent",
                    borderLeft: `3px solid ${isSelected ? "#1FB6C1" : "transparent"}`,
                  }}
                >
                  <span>
                    <span style={{ fontSize: "0.9rem", color: "#0C2A40", fontWeight: isSelected ? 600 : 500 }}>
                      {r.name}
                    </span>
                    <span className="ml-2" style={{ fontSize: "0.75rem", color: "rgba(12,42,64,0.55)" }}>
                      {r.region}
                    </span>
                  </span>
                  <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.5)", whiteSpace: "nowrap" }}>
                    {r.pshAnnual} / {r.pshWinter} h
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main calculator
// -----------------------------------------------------------------------------
export default function PVSolarCalculator() {
  // --- Pump
  const [pumpPreset, setPumpPreset] = useState("2.2");
  const [pumpCustomKw, setPumpCustomKw] = useState(2.2);
  const pumpKw = pumpPreset === "custom" ? pumpCustomKw : parseFloat(pumpPreset);

  const [qRatedLpm, setQRatedLpm] = useState(80);             // catalogue flow @ rated power & rated head
  const [qAt30Lpm,  setQAt30Lpm]  = useState(80 * Q30_RATIO_DEFAULT);
  const [flowUnit,  setFlowUnit]  = useState("lpm");          // "lpm" | "m3h"

  // Operating-point inputs (optional). Two physical inputs only:
  //   · hRatedM   = pump rated head (read straight from the pump curve)
  //   · hStaticM  = static lift + outlet pressure (the geometry of the site)
  // Friction is COMPUTED from pipe geometry below — not user-typed by default.
  // TDH = hStaticM + hFrictionRatedM is displayed as a result, not asked.
  const [hRatedM,         setHRatedM]         = useState(0);   // pump rated head (m)
  const [hStaticM,        setHStaticM]        = useState(0);   // static lift + outlet pressure (m)
  const [hFrictionRatedM, setHFrictionRatedM] = useState(0);   // friction at rated flow (m) — auto-filled by geometry

  // ---------- Inline Hazen-Williams friction estimator ----------
  // Single-pipe approximation for the 80 % case (one borehole → tank run).
  // Multi-tier networks should use /tools/hydraulics/network-friction.
  // The estimator is the PRIMARY path; "manualFrictionMode" lets a visitor
  // who already calculated friction externally type the value in directly.
  const [manualFrictionMode, setManualFrictionMode] = useState(false);
  const [pipeLenM,     setPipeLenM]     = useState(50);    // metres
  const [pipeIdMm,     setPipeIdMm]     = useState(50);    // mm inside diameter
  const [pipeMaterial, setPipeMaterial] = useState("hdpe");

  const pipeC = (PIPE_MATERIALS.find((m) => m.value === pipeMaterial) || PIPE_MATERIALS[0]).C;
  const pipeEstimate = useMemo(() => {
    if (!pipeLenM || !pipeIdMm || !qRatedLpm) return null;
    const id_m = pipeIdMm / 1000;
    const flow_m3s = qRatedLpm / 60000;            // L/min → m³/s
    const hL = hazenWilliamsHeadLoss({ flow_m3s, id_m, length_m: pipeLenM, C: pipeC });
    const v  = pipeVelocity({ flow_m3s, id_m });
    let warn = null;
    if (v > 3.0)      warn = "Above 3.0 m/s — erosive, water hammer risk. Step up to a larger ID.";
    else if (v > 2.5) warn = "Above 2.5 m/s — acceptable for short runs only.";
    else if (v < 0.6) warn = "Below 0.6 m/s — sedimentation risk on long horizontal runs.";
    return { hL, v, warn };
  }, [pipeLenM, pipeIdMm, qRatedLpm, pipeC]);

  // Default path: friction is driven from the pipe geometry estimator.
  // Power-user path: when manualFrictionMode is true, the visitor types
  // their own friction value and the geometry block is hidden.
  useEffect(() => {
    if (!manualFrictionMode && pipeEstimate && pipeEstimate.hL != null) {
      setHFrictionRatedM(Math.round(pipeEstimate.hL * 10) / 10);
    }
  }, [manualFrictionMode, pipeEstimate]);

  // TDH = static + friction. Computed for display only — the simulator
  // takes static and friction separately and recombines them per timestep.
  const hTdhM = hStaticM + hFrictionRatedM;

  // Headline derate factor at RATED flow — used for the badge / shutoff alert.
  // The actual simulation uses the dynamic per-timestep model.
  const opCorrectionAtRated = useMemo(
    () => correctFlowForSystemHead({ qRated: qRatedLpm, hRated: hRatedM, hSystem: hTdhM }),
    [qRatedLpm, hRatedM, hTdhM],
  );
  const correctionActive = hRatedM > 0 && (hStaticM > 0 || hFrictionRatedM > 0);
  const frictionActive   = correctionActive && hFrictionRatedM > 0;

  // Unit-aware helpers for the two pump-curve flow inputs.
  // Internally everything stays in L/min (the simulation's native unit) so
  // the toggle is purely a display affordance — no double-conversion drift.
  const LPM_PER_M3H = 1000 / 60;                              // 16.6̇ L/min in 1 m³/h
  const toDisplay = (lpm) =>
    flowUnit === "m3h" ? Math.round((lpm / LPM_PER_M3H) * 100) / 100 : lpm;
  const fromDisplay = (val) =>
    flowUnit === "m3h" ? val * LPM_PER_M3H : val;
  const flowUnitLabel    = flowUnit === "m3h" ? "m³/h" : "L/min";
  const flowUnitLabel30  = flowUnit === "m3h" ? "m³/h @ 30 %" : "L/min @ 30 %";

  // --- Location
  const [regionId, setRegionId] = useState("nelspruit");
  const region = PV_REGIONS.find((r) => r.id === regionId) || PV_REGIONS[0];

  // --- Panel
  const [panelPreset, setPanelPreset] = useState("550");
  const [panelCustomW, setPanelCustomW] = useState(550);
  const panelW = panelPreset === "custom" ? panelCustomW : parseFloat(panelPreset);
  const panelKw = panelW / 1000;

  // --- Daily target (optional — drives the recommendation row)
  const [targetLpd, setTargetLpd] = useState(20000);  // 20 m³/day default

  // --- Advanced
  const [etaPct, setEtaPct] = useState(ETA_DEFAULT_PCT);
  const [peakFactor, setPeakFactor] = useState(PEAK_FACTOR_DEFAULT);
  const [cutInPct, setCutInPct] = useState(CUT_IN_DEFAULT_PCT);
  const [daylightAnnual, setDaylightAnnual] = useState(DAYLIGHT_HOURS.annual);
  const [daylightWinter, setDaylightWinter] = useState(DAYLIGHT_HOURS.winter);

  // ---------- Staged simulation ----------
  // Generate a table from the peak-power floor up to 4× pump rating, stepping +2 panels.
  const stages = useMemo(() => {
    if (!pumpKw || !panelKw || !qRatedLpm) return [];
    const minArrayKwp = pumpKw * peakFactor;
    const minPanels = Math.max(2, Math.ceil(minArrayKwp / panelKw));
    const rows = [];
    const headParams = correctionActive
      ? { hRatedM, hStaticM, hFrictionRatedM }
      : {};
    for (let i = 0; i < 8; i++) {
      const panels = minPanels + i * 2;
      const arrayKwp = panels * panelKw;
      const winter = simulateSolarPumpDay({
        pumpKw, qRatedLpm, qAt30Lpm, cutInPct,
        arrayKwp, psh: region.pshWinter, daylightHours: daylightWinter,
        etaSystem: etaPct / 100,
        ...headParams,
      });
      const annual = simulateSolarPumpDay({
        pumpKw, qRatedLpm, qAt30Lpm, cutInPct,
        arrayKwp, psh: region.pshAnnual, daylightHours: daylightAnnual,
        etaSystem: etaPct / 100,
        ...headParams,
      });
      rows.push({
        panels, arrayKwp,
        winterHours: winter.pumpingHours, winterLpd: winter.volumeLitres, winterPeakLpm: winter.peakFlowLpm, winterKwh: winter.energyKwh,
        annualHours: annual.pumpingHours, annualLpd: annual.volumeLitres, annualPeakLpm: annual.peakFlowLpm, annualKwh: annual.energyKwh,
      });
    }
    return rows;
  }, [pumpKw, panelKw, qRatedLpm, qAt30Lpm, cutInPct, etaPct, peakFactor, region, daylightAnnual, daylightWinter, correctionActive, hRatedM, hStaticM, hFrictionRatedM]);

  // First stage row that meets the daily target on winter worst-case.
  const recommendedRow = useMemo(() => {
    if (!targetLpd || targetLpd <= 0) return null;
    return stages.find((s) => s.winterLpd >= targetLpd) || null;
  }, [stages, targetLpd]);

  return (
    <ToolShell
      slug="pv-solar"
      title="PV array sizing for solar pumps"
      oneLiner="This tool models the sun as a bell curve across daylight hours, the VFD cut-in dead-band at sunrise/sunset, and the pump's flow-vs-power curve. Stages panel-count → litres/day so you can see the actual return on each extra panel."
      onExport={() => {
        const ins = [
          { label: "Pump rating", value: pumpKw, unit: "kW" },
          { label: "Flow at rated power (catalogue)", value: toDisplay(qRatedLpm), unit: flowUnitLabel },
          { label: "Flow at 30 % power (catalogue)",  value: toDisplay(qAt30Lpm),  unit: flowUnitLabel },
          ...(correctionActive ? [
            { label: "Pump rated head",  value: hRatedM,  unit: "m head" },
            { label: "Static head (lift + outlet pressure)", value: hStaticM, unit: "m" },
            ...(hFrictionRatedM > 0 ? [
              { label: manualFrictionMode
                  ? "Friction loss at rated flow (manual entry)"
                  : `Friction loss at rated flow (${pipeLenM} m of ${pipeIdMm} mm ID, C=${pipeC})`,
                value: hFrictionRatedM, unit: "m friction (scales with Q^1.85)" },
            ] : []),
            { label: "Total dynamic head (static + friction)", value: fmt(hTdhM, 1), unit: "m" },
            { label: "Operating-point flow at rated head (after Q-H correction)",
              value: opCorrectionAtRated.shutoffReached ? "0 — TDH exceeds shutoff" : toDisplay(opCorrectionAtRated.qCorrected),
              unit: opCorrectionAtRated.shutoffReached ? "" : flowUnitLabel },
          ] : []),
          { label: "VFD cut-in threshold", value: cutInPct, unit: "% of rated" },
          { label: "Location", value: `${region.name} · ${region.region}` },
          { label: "Peak Sun Hours · annual", value: region.pshAnnual, unit: "h/day" },
          { label: "Peak Sun Hours · winter (June)", value: region.pshWinter, unit: "h/day" },
          { label: "Daylight · annual", value: daylightAnnual, unit: "h" },
          { label: "Daylight · winter", value: daylightWinter, unit: "h" },
          { label: "Panel wattage", value: panelW, unit: "W" },
          { label: "System efficiency", value: etaPct, unit: "%" },
          { label: "Peak-power factor", value: peakFactor, unit: "× pump kW" },
          { label: "Daily volume target", value: targetLpd, unit: "L/day" },
        ];
        const results = [];
        if (recommendedRow) {
          results.push(
            { label: "Recommended panels (winter meets target)", value: recommendedRow.panels, unit: "modules" },
            { label: "Recommended array", value: fmt(recommendedRow.arrayKwp, 2), unit: "kWp" },
            { label: "Winter delivery at recommendation", value: fmt(recommendedRow.winterLpd, 0), unit: "L/day",
              alts: [{ value: fmt(recommendedRow.winterLpd / 1000, 1), unit: "m³/day" }] },
            { label: "Annual avg delivery", value: fmt(recommendedRow.annualLpd, 0), unit: "L/day" },
            { label: "Winter pumping window", value: fmt(recommendedRow.winterHours, 1), unit: "h/day" },
          );
        } else if (targetLpd > 0) {
          results.push({ label: "Target not met by any row in the staged table",
            value: `Increase panel wattage, lower the target, or accept ${fmt(stages.at(-1)?.winterLpd, 0)} L/day on ${stages.at(-1)?.panels} panels.` });
        }
        stages.forEach((s) => {
          results.push({
            label: `${s.panels} panels (${fmt(s.arrayKwp, 2)} kWp)`,
            value: `winter ${fmt(s.winterLpd, 0)} L/day · ${fmt(s.winterHours, 1)} h  |  annual ${fmt(s.annualLpd, 0)} L/day · ${fmt(s.annualHours, 1)} h`,
          });
        });
        return {
          inputs: ins,
          results,
          notes: [
            "Reference only. The HidroVerse team of specialists will design the controller, MPPT settings, panel string layout and combiner/protection for your site.",
          ],
        };
      }}
    >
      {/* PUMP + LOCATION ROW */}
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Pump rating" hint="Standard IEC motor sizes. Choose 'Other' to type a nameplate kW.">
          <Select
            value={pumpPreset} onChange={setPumpPreset}
            options={[
              ...PUMP_SIZES_KW.map((k) => ({ value: String(k), label: `${k} kW` })),
              { value: "custom", label: "Other (type a kW value)" },
            ]}
            testid="pv-pump-preset"
          />
          {pumpPreset === "custom" && (
            <div className="mt-2">
              <SuffixNum value={pumpCustomKw} onChange={setPumpCustomKw} suffix="kW" step="0.01" min={0} testid="pv-pump-custom" />
            </div>
          )}
        </Field>

        <Field label="Site location" hint="Type to search SA + neighbouring towns. Picks the published winter / annual Peak Sun Hours for that area.">
          <LocationCombobox value={regionId} onChange={setRegionId} testid="pv-location" />
          <div className="mt-2 flex items-center gap-4 px-3 py-2"
            style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}>
            <div>
              <div className="font-mono uppercase" style={{ fontSize: "0.55rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.5)" }}>Annual PSH</div>
              <div style={{ fontSize: "0.95rem", color: "#0C2A40", fontWeight: 600 }} data-testid="pv-psh-annual">{region.pshAnnual} h/day</div>
            </div>
            <div>
              <div className="font-mono uppercase" style={{ fontSize: "0.55rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.5)" }}>Winter PSH</div>
              <div style={{ fontSize: "0.95rem", color: "#0C2A40", fontWeight: 600 }} data-testid="pv-psh-winter">{region.pshWinter} h/day</div>
            </div>
          </div>
          <div
            className="mt-2 font-light leading-relaxed"
            style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.6)" }}
          >
            PSH = day's total irradiance expressed as equivalent full-sun hours
            (the energy budget). The clock-hour pumping window in the table below
            is calculated from that budget — it's the time the array's DC output
            actually exceeds the pump cut-in.
          </div>
        </Field>
      </div>

      {/* PUMP CURVE ROW */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <div
            className="font-mono uppercase"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.65)" }}
          >
            Pump curve · flow at two anchor points
          </div>
          {/* Unit toggle pill — sits in the section eyebrow so it controls both
              flow inputs at once. Internal storage stays in L/min so the
              simulation is unaffected by the toggle. */}
          <div
            className="inline-flex rounded-full p-1"
            style={{ background: "rgba(12,42,64,0.06)", border: "1px solid rgba(12,42,64,0.10)" }}
            data-testid="pv-flow-unit-toggle"
          >
            {[
              { id: "lpm", label: "L/min" },
              { id: "m3h", label: "m³/h" },
            ].map((o) => {
              const active = flowUnit === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setFlowUnit(o.id)}
                  data-testid={`pv-flow-unit-${o.id}`}
                  className="px-3 py-1 rounded-full transition-colors"
                  style={{
                    background: active ? "#0C2A40" : "transparent",
                    color: active ? "#F5F7FA" : "rgba(12,42,64,0.7)",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={`Flow at rated power`} hint={`The pump's nameplate flow (Q at full rated kW). Most lodge / borehole pumps are ${flowUnit === "m3h" ? "1.8–15 m³/h" : "30–250 L/min"}.`}>
            <SuffixNum
              value={toDisplay(qRatedLpm)}
              onChange={(v) => {
                const lpm = fromDisplay(v);
                setQRatedLpm(lpm);
                setQAt30Lpm(Math.round(lpm * Q30_RATIO_DEFAULT * 10) / 10);
              }}
              suffix={flowUnitLabel}
              step={flowUnit === "m3h" ? "0.1" : "1"}
              min={0}
              testid="pv-qrated"
            />
          </Field>
          <Field label={`Flow at 30 % power`} hint={`Pump-curve anchor used to fit Q ∝ P^α. Defaults to ${(Q30_RATIO_DEFAULT * 100).toFixed(0)} % of rated, which matches a typical centrifugal under VFD speed control.`}>
            <SuffixNum
              value={toDisplay(qAt30Lpm)}
              onChange={(v) => setQAt30Lpm(fromDisplay(v))}
              suffix={flowUnitLabel30}
              step={flowUnit === "m3h" ? "0.01" : "0.1"}
              min={0}
              testid="pv-q30"
            />
          </Field>
        </div>
      </div>

      {/* OPERATING-POINT CORRECTION (optional)
          Catalogue Q is given at the pump's BEP — at one specific head.
          When the user enters their pump's rated head AND the system TDH,
          we shift the operating point along a parabolic Q-H curve and
          derate the rated flow accordingly. Without these values the
          simulation behaves exactly as before. */}
      <div className="mt-5">
        <div className="mb-3">
          <div
            className="font-mono uppercase"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.65)" }}
          >
            Operating-point correction · optional
          </div>
        </div>
        <p
          className="font-light leading-relaxed mb-3"
          style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}
        >
          Catalogue / nameplate flow is given at the pump's best-efficiency
          point — at one specific head. To match it to your actual site we
          need two physical numbers from you: the pump's <strong>rated head</strong>{" "}
          (off the pump curve) and the <strong>static head</strong> (the vertical
          lift from your water source up to the discharge, plus outlet pressure
          if any). Friction loss is computed from the pipe geometry below — you
          don't have to know it in advance. Total dynamic head = static + friction,
          and the simulation scales friction with flow at each timestep.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Pump rated head" hint="The head (m) at which the catalogue rated flow is published. On the pump curve, this is the head at the BEP / nameplate point.">
            <SuffixNum value={hRatedM} onChange={setHRatedM} suffix="m head" step="1" min={0} testid="pv-h-rated" />
          </Field>
          <Field label="Static head" hint="Vertical lift from the water source level up to the discharge level. Add outlet pressure if any (1 bar ≈ 10 m). This is the part of total dynamic head that does NOT change with flow.">
            <SuffixNum value={hStaticM} onChange={setHStaticM} suffix="m static" step="1" min={0} testid="pv-h-static" />
          </Field>
        </div>

        {/* ---------- Friction · pipe-geometry path (primary) ---------- */}
        {!manualFrictionMode && (
          <div
            className="mt-5 p-5"
            style={{ background: "#FCFCFA", border: "1px solid rgba(31,182,193,0.25)" }}
            data-testid="pv-pipe-estimator"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div
                className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.65)" }}
              >
                Friction loss · from pipe geometry
              </div>
              <button
                type="button"
                onClick={() => setManualFrictionMode(true)}
                data-testid="pv-friction-manual-toggle"
                className="font-mono uppercase transition-colors"
                style={{
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  color: "rgba(12,42,64,0.6)",
                  borderBottom: "1px dashed rgba(12,42,64,0.35)",
                  paddingBottom: 1,
                }}
              >
                I already know my friction loss
              </button>
            </div>
            <p
              className="font-light leading-relaxed mb-4"
              style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.7)" }}
            >
              Single-pipe Hazen-Williams. Friction scales with{" "}
              <span className="font-mono">L</span>,{" "}
              <span className="font-mono">1/D⁴·⁸⁷</span> and{" "}
              <span className="font-mono">Q¹·⁸⁵</span> — a 2 km run can demand a
              very different array than a 10 m run at the same flow. For
              multi-tier networks (mainline + sub-main + lateral) use{" "}
              <a
                href="/tools/hydraulics/network-friction"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#1FB6C1", textDecoration: "underline", textDecorationColor: "rgba(31,182,193,0.5)" }}
              >
                Network friction loss
              </a>{" "}
              and switch the toggle above to enter it directly.
            </p>

            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Pipe length" hint="Total run from pump outlet to discharge (single dominant pipe). Borehole-to-tank runs at lodges are typically 50–500 m; municipal trunks 1–5 km.">
                <SuffixNum value={pipeLenM} onChange={setPipeLenM} suffix="m" step="10" min={0} testid="pv-pipe-length" />
              </Field>
              <Field label="Inside diameter" hint="The INSIDE diameter of the pipe (mm). For HDPE class-10 50 mm OD the ID is ≈ 44 mm; for 63 mm OD ≈ 55 mm. Use the manufacturer's spec — nominal OD over-states ID by 5–15 %.">
                <SuffixNum value={pipeIdMm} onChange={setPipeIdMm} suffix="mm ID" step="1" min={1} testid="pv-pipe-id" />
              </Field>
              <Field label="Material" hint="Hazen-Williams roughness coefficient. HDPE / PVC are smooth (C = 150); galvanised steel ages to ~C = 120; older mild-steel mains drop to C = 100.">
                <Select
                  value={pipeMaterial}
                  onChange={setPipeMaterial}
                  options={PIPE_MATERIALS.map((m) => ({ value: m.value, label: m.label }))}
                  testid="pv-pipe-material"
                />
              </Field>
            </div>

            {pipeEstimate && (
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <div
                  className="px-4 py-3"
                  style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.10)" }}
                  data-testid="pv-pipe-estimate-friction"
                >
                  <div className="font-mono uppercase" style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
                    Friction at rated flow
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span style={{ fontSize: "1.4rem", fontWeight: 600, color: "#0C2A40" }}>
                      {fmt(pipeEstimate.hL, 1)}
                    </span>
                    <span className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.6)" }}>
                      m head · auto-applied
                    </span>
                  </div>
                  <div className="mt-1 font-light" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
                    {pipeLenM} m of {pipeIdMm} mm ID at C={pipeC}, Q={fmt(toDisplay(qRatedLpm), flowUnit === "m3h" ? 2 : 0)} {flowUnitLabel}
                  </div>
                </div>
                <div
                  className="px-4 py-3"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${pipeEstimate.warn ? "rgba(184,92,56,0.45)" : "rgba(12,42,64,0.10)"}`,
                  }}
                  data-testid="pv-pipe-estimate-velocity"
                >
                  <div className="font-mono uppercase" style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
                    Velocity check
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span style={{ fontSize: "1.4rem", fontWeight: 600, color: pipeEstimate.warn ? "#B85C38" : "#0C2A40" }}>
                      {fmt(pipeEstimate.v, 2)}
                    </span>
                    <span className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.6)" }}>m/s</span>
                  </div>
                  <div className="mt-1 font-light leading-snug" style={{ fontSize: "0.72rem", color: pipeEstimate.warn ? "#B85C38" : "rgba(12,42,64,0.55)" }}>
                    {pipeEstimate.warn || "Within the 0.6–2.5 m/s engineering envelope."}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------- Friction · manual entry path (power user) ---------- */}
        {manualFrictionMode && (
          <div
            className="mt-5 p-5"
            style={{ background: "#FCFCFA", border: "1px solid rgba(31,182,193,0.25)" }}
            data-testid="pv-friction-manual"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div
                className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.65)" }}
              >
                Friction loss · manual entry
              </div>
              <button
                type="button"
                onClick={() => setManualFrictionMode(false)}
                data-testid="pv-friction-geometry-toggle"
                className="font-mono uppercase transition-colors"
                style={{
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  color: "rgba(12,42,64,0.6)",
                  borderBottom: "1px dashed rgba(12,42,64,0.35)",
                  paddingBottom: 1,
                }}
              >
                Compute from pipe geometry instead
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Friction loss at rated flow" hint="Pipe friction at the pump's rated flow, in metres of head. Compute it with /tools/hydraulics/network-friction or your own spreadsheet, then paste it here. The simulator scales it with Q^1.85 at each timestep.">
                <SuffixNum value={hFrictionRatedM} onChange={setHFrictionRatedM} suffix="m friction" step="0.1" min={0} testid="pv-h-friction" />
              </Field>
            </div>
          </div>
        )}

        {/* ---------- TDH summary card ---------- */}
        {correctionActive && (
          <div
            className="mt-5 px-5 py-4 flex flex-wrap items-baseline gap-x-2 gap-y-1"
            style={{
              background: opCorrectionAtRated.shutoffReached && !frictionActive ? "#FFF5EE" : "#F3FBFB",
              border: `1px solid ${opCorrectionAtRated.shutoffReached && !frictionActive ? "rgba(184,92,56,0.30)" : "rgba(31,182,193,0.30)"}`,
            }}
            data-testid="pv-tdh-summary"
          >
            <div
              className="font-mono uppercase mr-3"
              style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
            >
              Total dynamic head
            </div>
            <span style={{ fontSize: "0.95rem", color: "#0C2A40" }}>
              static {fmt(hStaticM, 1)} m
            </span>
            <span className="font-mono" style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>+</span>
            <span style={{ fontSize: "0.95rem", color: "#0C2A40" }}>
              friction {fmt(hFrictionRatedM, 1)} m
            </span>
            <span className="font-mono" style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>=</span>
            <span style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0C2A40" }}>
              {fmt(hTdhM, 1)} m
            </span>
            <span className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>at rated flow</span>
            <span className="ml-auto font-light" style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.65)" }}>
              {opCorrectionAtRated.shutoffReached && !frictionActive
                ? "exceeds pump shutoff — pump cannot lift water"
                : opCorrectionAtRated.shutoffReached && frictionActive
                  ? "exceeds shutoff at rated Q — friction relaxes at part-load, see table"
                  : `Q derates to ${fmt(toDisplay(opCorrectionAtRated.qCorrected), flowUnit === "m3h" ? 2 : 1)} ${flowUnitLabel} (${fmt(opCorrectionAtRated.derateFactor * 100, 0)} %)`}
            </span>
          </div>
        )}
      </div>
      {/* PANEL + TARGET ROW */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <Field label="Panel wattage" hint="Pick the panel you can actually source. Wattage is the STC rating on the nameplate.">
          <Select
            value={panelPreset} onChange={setPanelPreset}
            options={[
              ...PV_PANEL_WATTAGES.map((p) => ({ value: String(p.w), label: p.label })),
              { value: "custom", label: "Other (type a watt value)" },
            ]}
            testid="pv-panel-preset"
          />
          {panelPreset === "custom" && (
            <div className="mt-2">
              <SuffixNum value={panelCustomW} onChange={setPanelCustomW} suffix="W" step="1" min={0} testid="pv-panel-custom" />
            </div>
          )}
        </Field>
        <Field label="Daily volume target" hint="Optional. Litres needed per day. The table below will mark the smallest panel count that meets this on winter worst-case.">
          <SuffixNum value={targetLpd} onChange={setTargetLpd} suffix="L/day target" step="100" min={0} testid="pv-target" />
        </Field>
      </div>

      {/* Advanced */}
      <details
        className="mt-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <summary
          className="cursor-pointer px-5 py-4 font-mono uppercase select-none"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.7)" }}
          data-testid="pv-advanced-toggle"
        >
          Advanced · controller, efficiency &amp; daylight
        </summary>
        <div className="px-5 pb-5 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Field label="System efficiency" hint="Umbrella factor: VFD/MPPT, temperature derate, wiring, soiling, mismatch. 75 % is a defensible SA default.">
            <SuffixNum value={etaPct} onChange={setEtaPct} suffix="%" step="1" min={1} testid="pv-eta" />
          </Field>
          <Field label="VFD cut-in threshold" hint="DC power as % of pump rated below which the pump won't start. Most solar VFD controllers sit in the 20–30 % range.">
            <SuffixNum value={cutInPct} onChange={setCutInPct} suffix="% of rated" step="1" min={1} testid="pv-cutin" />
          </Field>
          <Field label="Peak-power factor" hint="Minimum array kWp expressed as a multiple of pump kW. 1.30× is the standard PV-direct pumping rule.">
            <SuffixNum value={peakFactor} onChange={setPeakFactor} suffix="× pump kW" step="0.05" min={1} testid="pv-peak" />
          </Field>
          <Field label="Daylight · annual avg" hint="Sunrise → sunset clock hours. SA latitudes: equinox ≈ 12 h.">
            <SuffixNum value={daylightAnnual} onChange={setDaylightAnnual} suffix="h" step="0.1" min={6} testid="pv-daylight-annual" />
          </Field>
          <Field label="Daylight · winter (June)" hint="SA winter solstice ≈ 10.0–10.8 h depending on latitude.">
            <SuffixNum value={daylightWinter} onChange={setDaylightWinter} suffix="h" step="0.1" min={6} testid="pv-daylight-winter" />
          </Field>
        </div>
      </details>

      {/* RECOMMENDATION HEADLINE */}
      {targetLpd > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {recommendedRow ? (
            <>
              <Result
                label="Recommended panels"
                primary={{ value: recommendedRow.panels, unit: "modules" }}
                alts={[{ value: fmt(recommendedRow.arrayKwp, 2), unit: "kWp total" }]}
                note={`Smallest count that delivers ≥ ${fmt(targetLpd, 0)} L/day on June worst-case.`}
                testid="pv-result-rec-panels"
              />
              <Result
                label="Winter delivery"
                primary={{ value: fmt(recommendedRow.winterLpd, 0), unit: "L/day" }}
                alts={[{ value: fmt(recommendedRow.winterLpd / 1000, 1), unit: "m³/day" }]}
                note={`Pump runs ${fmt(recommendedRow.winterHours, 1)} h of clock-time.`}
                testid="pv-result-rec-winter"
              />
              <Result
                label="Annual avg delivery"
                primary={{ value: fmt(recommendedRow.annualLpd, 0), unit: "L/day" }}
                alts={[
                  { value: fmt(recommendedRow.annualLpd / 1000, 1), unit: "m³/day" },
                  { value: fmt(recommendedRow.annualKwh, 1), unit: "kWh/day to pump" },
                ]}
                note={`Pump runs ${fmt(recommendedRow.annualHours, 1)} h. Typical year-round day.`}
                testid="pv-result-rec-annual"
              />
              <Result
                label="Winter energy to pump"
                primary={{ value: fmt(recommendedRow.winterKwh, 1), unit: "kWh/day" }}
                alts={[{ value: fmt(toDisplay(recommendedRow.winterPeakLpm), flowUnit === "m3h" ? 2 : 1), unit: `${flowUnitLabel} peak flow` }]}
                note="Energy that actually drives water (= ∫ DC kW while pump is on)."
                testid="pv-result-rec-energy"
              />
            </>
          ) : (
            <div className="col-span-full p-5"
              style={{ background: "#FFF5EE", border: "1px solid rgba(184,92,56,0.30)" }}>
              <div className="font-mono uppercase mb-2" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#B85C38" }}>
                Target not met
              </div>
              <div style={{ fontSize: "0.9rem", color: "#0C2A40", lineHeight: 1.55 }}>
                None of the staged panel counts deliver {fmt(targetLpd, 0)} L/day on winter worst-case at this site.
                {stages.length > 0 && (
                  <> Largest row in the table — {stages.at(-1).panels} panels — gives {fmt(stages.at(-1).winterLpd, 0)} L/day.</>
                )}{" "}
                Increase the panel wattage, accept a lower winter target, or talk to the HidroVerse team about a larger pump or storage to bridge sunless days.
              </div>
            </div>
          )}
        </div>
      )}

      {/* STAGED TABLE */}
      <div className="mt-6" data-testid="pv-staged-table">
        <div className="font-mono uppercase mb-3"
          style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.65)" }}>
          Panels → litres · winter worst-case &amp; annual avg
        </div>
        <div className="overflow-x-auto" style={{ border: "1px solid rgba(12,42,64,0.10)" }}>
          <table className="w-full" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0C2A40", color: "#F5F7FA" }}>
                <th className="text-left px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Panels</th>
                <th className="text-left px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Array</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Winter kWh</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Winter L/day</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Winter hrs</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Annual kWh</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Annual L/day</th>
                <th className="text-right px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Annual hrs</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((s, idx) => {
                const isRec = recommendedRow && s.panels === recommendedRow.panels;
                const meets = targetLpd > 0 && s.winterLpd >= targetLpd;
                return (
                  <tr
                    key={s.panels}
                    data-testid={`pv-stage-row-${s.panels}`}
                    style={{
                      background: isRec ? "rgba(31,182,193,0.10)"
                        : (idx % 2 === 0 ? "#FFFFFF" : "#FAFAF7"),
                      borderTop: "1px solid rgba(12,42,64,0.06)",
                      borderLeft: isRec ? "3px solid #1FB6C1" : "3px solid transparent",
                    }}
                  >
                    <td className="px-3 py-2.5" style={{ fontSize: "0.88rem", color: "#0C2A40", fontWeight: isRec ? 700 : 500 }}>
                      {s.panels}
                      {isRec && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 font-mono uppercase"
                          style={{ background: "#1FB6C1", color: "#070A0D", fontSize: "0.55rem", letterSpacing: "0.15em", borderRadius: 3 }}>
                          Recommended
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5" style={{ fontSize: "0.86rem", color: "#0C2A40" }}>
                      {fmt(s.arrayKwp, 2)} kWp
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.7)" }}>
                      {fmt(s.winterKwh, 1)}
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.88rem", color: meets ? "#0C2A40" : "rgba(12,42,64,0.7)", fontWeight: meets ? 600 : 500 }}>
                      {fmt(s.winterLpd, 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.7)" }}>
                      {fmt(s.winterHours, 1)} h
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.7)" }}>
                      {fmt(s.annualKwh, 1)}
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.88rem", color: "#0C2A40" }}>
                      {fmt(s.annualLpd, 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.7)" }}>
                      {fmt(s.annualHours, 1)} h
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 font-light leading-relaxed" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.6)" }}>
          Each row is a separate physics simulation: array kWp × half-sine irradiance × pump-curve flow, with the VFD cut-in dead-band at sunrise / sunset taken into account. Notice the diminishing returns — beyond a certain count the controller clips midday excess and extra panels stop paying for themselves.
        </p>
      </div>

      <p className="mt-6 font-light leading-relaxed" style={{ fontSize: "0.82rem", color: "rgba(12,42,64,0.6)" }}>
        Reference only — the team of HidroVerse specialists will design the controller, MPPT
        settings, string layout, combiner box and DC / surge protection that turn this
        sizing into a working installation.
      </p>
    </ToolShell>
  );
}
